import { Search } from '../../types';

/**
 * A row in the searches list: either a standalone search or a "pack" of searches
 * created together via Multi-Agent Search (sharing a `groupId`).
 */
export type SearchRow =
  | { kind: 'single'; search: Search }
  | { kind: 'pack'; groupId: string; searches: Search[] };

/**
 * Lay searches out into display rows, collapsing those that share a `groupId`
 * into a single pack. A pack is anchored at the position of its first-seen member
 * (so overall ordering is preserved), and gathers later members even if the list
 * isn't strictly contiguous. A group that ends up with only one member (e.g. its
 * siblings were deleted, or the rest were skipped as duplicates) renders as a
 * plain single — no pack chrome for a pack of one.
 */
export function groupSearches(searches: Search[]): SearchRow[] {
  const groups = new Map<string, Search[]>();
  const layout: ({ type: 'group'; id: string } | { type: 'single'; search: Search })[] = [];

  for (const search of searches) {
    if (search.groupId) {
      const existing = groups.get(search.groupId);
      if (existing) {
        existing.push(search);
      } else {
        groups.set(search.groupId, [search]);
        layout.push({ type: 'group', id: search.groupId });
      }
    } else {
      layout.push({ type: 'single', search });
    }
  }

  return layout.map((row) => {
    if (row.type === 'single') {
      return { kind: 'single', search: row.search };
    }
    const members = groups.get(row.id) as Search[];
    return members.length === 1
      ? { kind: 'single', search: members[0] }
      : { kind: 'pack', groupId: row.id, searches: members };
  });
}
